import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Button,
  IconWithTooltip,
  Attribute,
  Tooltip,
} from "@/components/atoms";
import {
  useUserContext,
  useCompanyContext,
  useJobPostContext,
} from "@/providers/contexts";
import BookmarkIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkAddedIcon from "@mui/icons-material/Bookmark";
import { JobPost } from "@/types/jobPost";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ShareIcon from "@mui/icons-material/Share";
import { Department } from "@/types/jobAttributes";

interface JobCardProps {
  jobPost: JobPost;
}

export const JobCard = ({ jobPost }: JobCardProps) => {
  const id = jobPost.id;
  const company = jobPost.company;
  const job_title = jobPost.job_title;
  const job_posted = jobPost.job_posted;
  const job_updated = jobPost.job_updated;
  const job_setting = jobPost.job_setting;
  const department = jobPost.department;
  const locations = jobPost.locations;
  const job_salary_min = jobPost.job_salary_min;
  const job_salary_max = jobPost.job_salary_max;
  const job_salary_single = jobPost.job_salary_single;
  const educations = jobPost.educations;
  const experiences = jobPost.experiences;
  const credentials = jobPost.credentials;
  const job_qualifications = jobPost.job_qualifications;
  const job_responsibilities = jobPost.job_responsibilities;
  const job_url = jobPost.job_url;
  const job_salary_currency = jobPost.job_salary_currency;

  /* --------------------- Hooks --------------------- */
  const { setCompanyModalOpen, setCompanyId } = useCompanyContext();
  const { setFlagJobPostModalOpen, setJobPostId, setRecentlyAppliedJobId } =
    useJobPostContext();

  const {
    isAuthenticated,
    disabledBasicContent,
    disabledProContent,
    toggleSaveJobPost,
    jobPostError,
    isJobSaved,
    savedJobs,
    isSuperUser,
  } = useUserContext();

  /* --------------------- States --------------------- */

  const [isBookmarked, setIsBookmarked] = useState(isJobSaved(id));
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success",
  );

  /* --------------------- Constants --------------------- */
  const inactiveJob = jobPost.job_active === false;

  const disabledContent =
    disabledBasicContent || disabledProContent || !isAuthenticated;

  const jobPostDate = job_posted
    ? new Date(job_posted).toLocaleDateString()
    : "";

  const jobUpdatedDate = job_updated
    ? new Date(job_updated).toLocaleDateString()
    : null;

  const domainsLabel =
    company.healthcare_domains && company.healthcare_domains.length > 1
      ? "Healthcare Domains"
      : "Healthcare Domain";

  const salary_range =
    job_salary_min && job_salary_max
      ? `${job_salary_min} - ${job_salary_max}`
      : job_salary_single;

  /* --------------------- Handles --------------------- */

  const handleBookmarkClick = async () => {
    try {
      toggleSaveJobPost(id);

      setSnackbarMessage(
        !isBookmarked ? "Job added to bookmarks" : "Job removed from bookmarks",
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage("Something went wrong");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleFlagErrorClick = () => {
    setJobPostId(id);
    setFlagJobPostModalOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleCompanyClick = () => {
    setCompanyModalOpen(true);
    setCompanyId(company?.id);
  };

  const handleApplyClick = () => {
    // So when they return, applied modal opens
    // this should not happen for non-registered users
    if (isAuthenticated) {
      setRecentlyAppliedJobId(id);
    }
    window.open(job_url, "_blank", "noopener,noreferrer");
  };

  const handleShareClick = async () => {
    const queryParams = new URLSearchParams();

    if (job_title) queryParams.append("title", job_title);

    if (company?.company_name)
      queryParams.append("company", company.company_name);

    if (locations)
      queryParams.append(
        "location",
        Array.isArray(locations) ? locations.join(", ") : locations,
      );

    if (job_salary_min !== null && job_salary_max !== null) {
      queryParams.append("salary", `$${job_salary_min} - $${job_salary_max}`);
    } else if (job_salary_single !== null) {
      queryParams.append("salary", `$${job_salary_single}`);
    }

    if (job_posted)
      queryParams.append("posted", new Date(job_posted).toLocaleDateString());

    if (job_updated)
      queryParams.append("updated", new Date(job_updated).toLocaleDateString());

    if (job_setting && job_setting.length > 0)
      queryParams.append("setting", job_setting.join(", "));

    if (department) queryParams.append("department", department);

    if (isSuperUser) {
      if (job_salary_currency)
        queryParams.append("currency", job_salary_currency);

      if (educations && educations.length > 0)
        queryParams.append("education", educations.join(", "));

      if (experiences && experiences.length > 0)
        queryParams.append("experience", experiences.join(", "));

      if (credentials && credentials.length > 0)
        queryParams.append("credentials", credentials.join(", "));

      if (job_qualifications && job_qualifications.length > 0) {
        const topQuals = job_qualifications.slice(0, 3).join(", ");
        queryParams.append("qualifications", topQuals);
      }

      if (job_responsibilities && job_responsibilities.length > 0) {
        const topResps = job_responsibilities.slice(0, 3).join(", ");
        queryParams.append("responsibilities", topResps);
      }
    }

    queryParams.append("id", id.toString());

    const shareUrl = `https://job.pharmdsinit.com/share/job?${queryParams.toString()}`;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
        setSnackbarMessage("Link copied to clipboard!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } else {
        fallbackCopyToClipboard(shareUrl);
      }
    } catch (err) {
      setSnackbarMessage("Failed to share the job");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // fallback method for non-mac users
  const fallbackCopyToClipboard = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.opacity = "0";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (successful) {
        setSnackbarMessage("Link copied to clipboard!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } else {
        throw new Error("Copy command was unsuccessful");
      }
    } catch (err) {
      console.error("Fallback: Unable to copy", err);
    }

    document.body.removeChild(textArea);
  };

  /* --------------------- Lifecycle Methods --------------------- */

  useEffect(() => {
    if (jobPostError) {
      setSnackbarMessage(jobPostError);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [jobPostError]);

  useEffect(() => {
    setIsBookmarked(isJobSaved(id));
  }, [savedJobs, id, isJobSaved]);

  const responsibilitiesRaw = job_responsibilities;

  const responsibilities: string[] = Array.isArray(responsibilitiesRaw)
    ? responsibilitiesRaw
    : typeof responsibilitiesRaw === "string"
      ? [responsibilitiesRaw]
      : [];

  const displayResponsibilities = responsibilities
    .map(r => String(r).trim())
    .filter(Boolean)
    .slice(0, 3);

  const value =
    responsibilities.length > 3
      ? [...displayResponsibilities, "..."]
      : displayResponsibilities;

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        border: "1px solid #226f54",
        borderRadius: "2px",
      }}
      data-testid={`job-card-${id}`}
    >
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <CardContent>
        <Grid container direction="row" justifyContent="space-between">
          <Grid item>
            <Typography variant="h6">{job_title}</Typography>
          </Grid>

          <Grid item display="flex" alignItems="center" gap={1}>
            <IconWithTooltip
              componentName="job-card-share-icon"
              IconComponent={ShareIcon}
              tooltipText="Share this job"
              onClick={handleShareClick}
            />

            <IconWithTooltip
              componentName="job-card-flag-icon"
              IconComponent={ReportProblemIcon}
              tooltipText="Report this job post if it is errored"
              onClick={handleFlagErrorClick}
              disabledContent={!isAuthenticated}
            />
            {isBookmarked ? (
              <IconWithTooltip
                componentName="job-card-bookmark-added-icon"
                IconComponent={BookmarkAddedIcon}
                tooltipText="Remove this saved job from bookmarks"
                onClick={handleBookmarkClick}
                disabledContent={disabledContent}
              />
            ) : (
              <IconWithTooltip
                componentName="job-card-bookmark-icon"
                IconComponent={BookmarkIcon}
                tooltipText="Save this Job"
                onClick={handleBookmarkClick}
                disabledContent={disabledContent}
              />
            )}
          </Grid>
        </Grid>
        <Typography
          variant="body2"
          color="primary.main"
          sx={{
            mb: 1,
            cursor: "pointer",
            fontWeight: "bold",
            maxWidth: "fit-content",
          }}
          onClick={handleCompanyClick}
        >
          {company.company_name || "Unknown Company"}
        </Typography>
        <Attribute
          componentName={"healthcare-domain"}
          label={domainsLabel}
          value={company?.healthcare_domains}
          renderChip
        />
        {job_setting.length != 0 && (
          <Attribute
            componentName={"job-setting"}
            label="Setting"
            value={job_setting}
          />
        )}
        {locations?.length != 0 && (
          <Attribute
            componentName={"job-location"}
            label="Location(s)"
            value={locations || "N/A"}
          />
        )}

        {department && (
          <Attribute
            componentName={"department"}
            label="Department"
            value={
              department === ("it" as Department["dept_name"])
                ? "information technology"
                : department
            }
          />
        )}

        {salary_range !== null && salary_range !== 0 ? (
          <Attribute
            componentName={"salary"}
            label="Salary"
            value={salary_range as string}
          />
        ) : (
          <Attribute
            componentName={"salary"}
            label="Salary"
            value="Not provided by job post"
          />
        )}

        {educations?.length != 0 && (
          <Attribute
            componentName={"education"}
            label="Education"
            value={educations as string[]}
          />
        )}

        {experiences?.length != 0 && (
          <Attribute
            componentName={"experience"}
            label="Experience"
            value={experiences as string[]}
          />
        )}

        {job_responsibilities && job_responsibilities?.length !== 0 && (
          <Attribute
            componentName="responsibilities"
            label="Responsibilities"
            value={value}
          />
        )}

        {job_qualifications?.length !== 0 && (
          <Attribute
            componentName="qualifications"
            label="Qualifications"
            value={(job_qualifications as string[])
              ?.map(item => item.trim())
              .slice(0, 3)
              .concat(
                (job_qualifications as string[]).length > 3 ? ["..."] : [],
              )}
          />
        )}

        <Box
          mt={2}
          textAlign="center"
          sx={{
            float: "left",
          }}
        >
          <Attribute
            componentName={"posted-date"}
            label="Posted Date"
            value={jobPostDate || "Not provided by job post"}
          />
          <Attribute
            componentName={"updated-date"}
            label="Last Updated Date"
            value={jobUpdatedDate ?? "Not provided by job post"}
          />
        </Box>

        <Box
          mt={3}
          textAlign="center"
          sx={{
            float: "right",
          }}
        >
          {inactiveJob ? (
            <Tooltip
              componentName={`job-card-apply-button`}
              tooltipMessage="This job is no longer active on the company website"
            >
              <span>
                <Button
                  componentName="job-card-apply-button"
                  variant="contained"
                  color="primary"
                  onClick={handleApplyClick}
                  disabled={inactiveJob}
                >
                  Apply
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              componentName="job-card-apply-button"
              variant="contained"
              color="primary"
              onClick={handleApplyClick}
            >
              Apply
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
